import { WSLabelProps } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccount } from "../../../../query/banking/queries/useQueryInternalAccount";
import { useQueryInternalAccountBalance } from "../../../../query/banking/queries/useQueryInternalAccountBalance";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPaymentsCard } from "../../../../query/paymentCards/queries/useQueryPaymentsCard";
import { useQueryPayoutSettingsDebitCard } from "../../../../query/payments/queries";
import {
  useQueryAccount,
  useClientQuery
} from "../../../../query/users/queries";
import { avatarBankAccount } from "../../../../shared/constants/avatars";
import { MethodType } from "../ManageMethods/types";
import {
  getAccountLabelProps,
  getCheckbookCardLabelProps,
  getCreditCardLabelProps,
  getInternalAccountLabelProps,
  getPaymentCardLabelProps
} from "../ManageMethods/utils";

const emptyLabel = {
  text: "Account",
  avatar: avatarBankAccount
};

export function useMethodLabelProps(
  id?: string,
  type?: MethodType,
  options?: {
    showBalance?: boolean;
  }
) {
  const history = useHistory();
  const userId = useUserId();

  const queryAccount = useQueryAccount(id as string, {
    enabled: !!id && type === "Account"
  });
  const queryInternalAccount = useQueryInternalAccount(id as string, {
    enabled: !!id && type === "InternalAccount"
  });
  const queryInternalAccountBalance = useQueryInternalAccountBalance(
    id as string,
    {
      enabled: !!id && type === "InternalAccount" && options?.showBalance
    }
  );
  const queryClient = useClientQuery(userId, {
    enabled: !!id && type === "CreditCard"
  });
  const queryCheckbookCard = useQueryPayoutSettingsDebitCard(
    userId,
    id as string,
    {
      enabled: !!id && type === "CheckbookCard"
    }
  );
  const queryPaymentCard = useQueryPaymentsCard(id as string, {
    enabled: !!id && type === "PaymentCard"
  });

  const isLoading = useMemo(() => {
    if (type === "Account") {
      return queryAccount.isLoading;
    }
    if (type === "InternalAccount") {
      return queryInternalAccount.isLoading;
    }
    if (type === "CreditCard") {
      return queryClient.isLoading;
    }
    if (type === "CheckbookCard") {
      return queryCheckbookCard.isLoading;
    }
    if (type === "PaymentCard") {
      return queryPaymentCard.isLoading;
    }

    return undefined;
  }, [
    type,
    queryAccount.isLoading,
    queryInternalAccount.isLoading,
    queryClient.isLoading,
    queryCheckbookCard.isLoading,
    queryPaymentCard.isLoading
  ]);

  const label = useMemo((): WSLabelProps | undefined => {
    if (type === "Account" && queryAccount.data) {
      return getAccountLabelProps(queryAccount.data);
    }

    if (type === "InternalAccount" && queryInternalAccount.data) {
      return getInternalAccountLabelProps(
        queryInternalAccount.data,
        history.push,
        options?.showBalance
          ? (queryInternalAccountBalance.data?.available ??
              queryInternalAccountBalance.data?.balance)
          : undefined
      );
    }

    if (type === "CreditCard") {
      const creditCard = queryClient.data?.profile.savedPaymentMethods?.find(
        creditCard => creditCard.paymentMethodId === id
      );

      if (creditCard) {
        return getCreditCardLabelProps(creditCard);
      }
    }

    if (type === "CheckbookCard" && queryCheckbookCard.data) {
      return getCheckbookCardLabelProps(queryCheckbookCard.data);
    }

    if (type === "PaymentCard" && queryPaymentCard.data) {
      return getPaymentCardLabelProps(queryPaymentCard.data);
    }

    if (id) {
      return emptyLabel;
    }
  }, [
    history.push,
    id,
    options?.showBalance,
    queryAccount.data,
    queryCheckbookCard.data,
    queryClient.data?.profile.savedPaymentMethods,
    queryInternalAccount.data,
    queryInternalAccountBalance.data?.available,
    queryInternalAccountBalance.data?.balance,
    queryPaymentCard.data,
    type
  ]);

  return { isLoading, label };
}
